/**
 * Утилиты для безопасного извлечения данных из DTO после валидации
 */

/**
 * Безопасно извлекает поле data из DTO
 * @param parsed - Результат валидации (может быть валидным или исходными данными)
 * @param dto - Исходные данные
 * @param fallback - Значение по умолчанию, если data не найдено
 */
export const extractData = <T>(
  parsed: unknown,
  dto: unknown,
  fallback?: T,
): T => {
  if (parsed && typeof parsed === "object" && "data" in parsed) {
    return (parsed as { data: T }).data;
  }

  if (dto && typeof dto === "object" && "data" in dto) {
    return (dto as { data?: T }).data as T;
  }

  return (fallback ?? dto) as T;
};

/**
 * Безопасно извлекает поля data и meta из DTO
 * @param parsed - Результат валидации
 * @param dto - Исходные данные
 * @param defaultMeta - Значение meta по умолчанию
 */
export const extractDataWithMeta = <TData, TMeta>(
  parsed: unknown,
  dto: unknown,
  defaultMeta: TMeta,
): { data: TData; meta: TMeta } => {
  if (
    parsed &&
    typeof parsed === "object" &&
    "data" in parsed &&
    "meta" in parsed
  ) {
    return {
      data: (parsed as { data: TData; meta: TMeta }).data,
      meta: (parsed as { data: TData; meta: TMeta }).meta,
    };
  }

  // Fallback на исходные данные
  const fallback = dto as { data?: TData; meta?: TMeta };
  return {
    data: (fallback?.data || []) as TData,
    meta: (fallback?.meta || defaultMeta) as TMeta,
  };
};

/**
 * Безопасно извлекает single data из DTO
 * @param parsed - Результат валидации
 * @param dto - Исходные данные
 * @returns Single data или null
 */
export const extractSingleData = <T>(
  parsed: unknown,
  dto: unknown,
): T | null => {
  // Если parsed уже содержит data в правильном формате
  if (parsed && typeof parsed === "object" && "data" in parsed) {
    const data = (parsed as { data: T }).data;
    return data || null;
  }

  // Если dto содержит data
  if (dto && typeof dto === "object" && "data" in dto) {
    const data = (dto as { data?: T }).data;
    return data || null;
  }

  // Fallback: если dto уже плоский объект
  return (dto as T) || null;
};
